import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  EmojiEvents,
  Check,
  Sync,
  Security,
  Schedule,
  Bolt,
  EventBusy,
  Map,
  ChevronLeft,
  ChevronRight,
  GetApp,
  HowToReg as HowToRegIcon,
  ArrowForward as ArrowForwardIcon,
  PlayArrow as PlayArrowIcon,
  Close as CloseIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useKonamiCode } from '@/hooks/useKonamiCode';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import FregPage from './FregPage';
import Footer from '@/components/Footer';
import api from '@/services/api';

// ── Shared constants ────────────────────────────────────────────────
const FONT = '"Inter", system-ui, -apple-system, sans-serif';
const ACCENT = '#10b981'; // emerald-500
const ACCENT_DARK = '#059669'; // emerald-600
const SURFACE = '#fafafa';

// ── CSS keyframes (injected once) ───────────────────────────────────
const STYLE_ID = 'new-landing-keyframes';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes nl-fade-up {
      from { opacity: 0; transform: translateY(32px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes nl-fade-in {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes nl-marquee {
      from { transform: translateX(0); }
      to   { transform: translateX(-50%); }
    }
  `;
  document.head.appendChild(style);
}

// ── League logos ────────────────────────────────────────────────────
// Each logo is tagged by shape so the marquee can size per-kind — badges
// (square crests) render taller than wordmarks (wide text lockups) so they
// carry roughly equal visual weight. Order separates GA Aspire from GA so
// their different internal text scales don't sit adjacent.
import centralStatesLogo from '@/assets/logos/central-states-npl.png';
import dplLogo from '@/assets/logos/dpl-logo.png';
import gaAspireLogo from '@/assets/logos/girls-academy-aspire-logo.png';
import ecnlRlLogo from '@/assets/logos/ecnl-rl.png';
import ecnlLogo from '@/assets/logos/ecnl.png';
import gaLogo from '@/assets/logos/girls-academy-logo.png';
import mlsNextLogo from '@/assets/logos/mls-next.png';
import nplLogo from '@/assets/logos/npl-logo.webp';
import southAtlanticLogo from '@/assets/logos/south-atlantic-premiere-league.png';
import usYouthLogo from '@/assets/logos/us-youth-soccer.png';
import uslYLogo from '@/assets/logos/usl-y-logo.webp';

type LogoKind = 'badge' | 'lockup' | 'wordmark';
const LEAGUE_LOGOS: { src: string; kind: LogoKind; alt: string }[] = [
  { src: centralStatesLogo,  kind: 'badge',    alt: 'Central States NPL' },
  { src: dplLogo,            kind: 'badge',    alt: 'Development Player League' },
  { src: gaAspireLogo,       kind: 'wordmark', alt: 'Girls Academy Aspire' },
  { src: ecnlRlLogo,         kind: 'lockup',   alt: 'ECNL Regional League' },
  { src: ecnlLogo,           kind: 'badge',    alt: 'ECNL' },
  { src: gaLogo,             kind: 'wordmark', alt: 'Girls Academy' },
  { src: mlsNextLogo,        kind: 'badge',    alt: 'MLS Next' },
  { src: nplLogo,            kind: 'badge',    alt: 'NPL' },
  { src: southAtlanticLogo,  kind: 'badge',    alt: 'South Atlantic Premier League' },
  { src: usYouthLogo,        kind: 'lockup',   alt: 'US Youth Soccer' },
  { src: uslYLogo,           kind: 'wordmark', alt: 'USL Y' },
];
const LOGO_SIZE: Record<LogoKind, { h: { xs: number; md: number }; maxW?: number }> = {
  badge:    { h: { xs: 50, md: 64 } },
  lockup:   { h: { xs: 40, md: 52 }, maxW: 180 },
  wordmark: { h: { xs: 32, md: 44 }, maxW: 240 },
};

// ── Scroll-triggered fade-in hook ───────────────────────────────────
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.15 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
}

const RevealBox: React.FC<{ children: React.ReactNode; delay?: number; sx?: object }> = ({ children, delay = 0, sx }) => {
  const { ref, visible } = useReveal();
  return (
    <Box
      ref={ref}
      sx={{
        opacity: 0,
        ...(visible && {
          animation: `nl-fade-up 0.7s ${delay}s ease both`,
        }),
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

// ── Image carousel ──────────────────────────────────────────────────
const ImageCarousel: React.FC = () => {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hovering, setHovering] = useState(false);

  const images = [
    { src: '/screenshots/dashboard.png', alt: 'Dashboard overview' },
    { src: '/screenshots/players.png', alt: 'Player management' },
    { src: '/screenshots/schedules.png', alt: 'Schedule calendar' },
    { src: '/screenshots/leagues.png', alt: 'League management' },
  ];

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setCurrent((p) => (p + 1) % images.length), 6000);
    return () => clearInterval(id);
  }, [images.length, paused]);

  const interact = (fn: () => void) => {
    fn();
    setPaused(true);
    setTimeout(() => setPaused(false), 10000);
  };

  return (
    <Box
      sx={{ position: 'relative', width: '100%', height: '100%' }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      {images.map((img, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute', inset: 0,
            opacity: current === i ? 1 : 0,
            transition: 'opacity 0.6s ease',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          }}
        >
          <img src={img.src} alt={img.alt} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </Box>
      ))}

      {/* Nav arrows */}
      {[
        { icon: <ChevronLeft />, side: 'left', fn: () => setCurrent((p) => (p - 1 + images.length) % images.length) },
        { icon: <ChevronRight />, side: 'right', fn: () => setCurrent((p) => (p + 1) % images.length) },
      ].map(({ icon, side, fn }) => (
        <IconButton
          key={side}
          onClick={() => interact(fn)}
          sx={{
            position: 'absolute', [side]: 12, top: '50%', transform: 'translateY(-50%)',
            bgcolor: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(4px)',
            opacity: hovering ? 1 : 0, transition: 'opacity 0.3s',
            '&:hover': { bgcolor: 'white' },
          }}
        >
          {icon}
        </IconButton>
      ))}

      {/* Progress dots */}
      <Box sx={{
        position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)',
        display: 'flex', gap: 0.75,
        opacity: hovering ? 1 : 0.3, transition: 'opacity 0.3s',
      }}>
        {images.map((_, i) => (
          <Box
            key={i}
            onClick={() => interact(() => setCurrent(i))}
            sx={{
              width: current === i ? 24 : 8, height: 8,
              borderRadius: 99, cursor: 'pointer',
              bgcolor: current === i ? 'white' : 'rgba(255,255,255,0.5)',
              transition: 'all 0.3s ease',
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

// ── Main page ───────────────────────────────────────────────────────
const LandingPage: React.FC = () => {
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [signupsEnabled, setSignupsEnabled] = useState<boolean | null>(null);
  const [waitlistEnabled, setWaitlistEnabled] = useState<boolean | null>(null);
  const [interestDialogOpen, setInterestDialogOpen] = useState(false);
  const [interestEmail, setInterestEmail] = useState('');
  const [interestLoading, setInterestLoading] = useState(false);
  const [interestError, setInterestError] = useState<string | null>(null);
  const [interestSuccess, setInterestSuccess] = useState<string | null>(null);
  const navigate = useNavigate();
  const konamiActivated = useKonamiCode();
  const { canInstall, install } = usePWAInstall('landing');

  useEffect(() => {
    const check = async () => {
      try {
        const r = await api.get('/auth/signup-status');
        setSignupsEnabled(r.data.signupsEnabled);
        setWaitlistEnabled(r.data.waitlistEnabled ?? false);
      } catch {
        setSignupsEnabled(true);
        setWaitlistEnabled(false);
      }
    };
    check();
  }, []);

  const handleRegisterInterest = async () => {
    if (!interestEmail) { setInterestError('Please enter your email address'); return; }
    setInterestLoading(true);
    setInterestError(null);
    setInterestSuccess(null);
    try {
      const r = await api.post('/app-interest', { email: interestEmail });
      setInterestSuccess(r.data.message);
      setInterestEmail('');
    } catch (err: any) {
      setInterestError(err.response?.data?.error?.message || 'Failed to register interest. Please try again.');
    } finally {
      setInterestLoading(false);
    }
  };

  const handleCloseInterestDialog = () => {
    setInterestDialogOpen(false);
    setInterestEmail('');
    setInterestError(null);
    setInterestSuccess(null);
  };

  if (konamiActivated) return <FregPage />;

  const handleGetStarted = () => {
    if (signupsEnabled) navigate('/register');
    else if (waitlistEnabled) setInterestDialogOpen(true);
  };

  // ── Pill button helper ──────────────────────────────────────────
  const pill = { borderRadius: '999px', textTransform: 'none' as const, fontFamily: FONT, fontWeight: 600, px: 3.5 };

  const features = [
    { icon: Sync, title: 'Schedule Connect', desc: 'Link your leagues and tournaments. Schedules stay updated automatically for every team.' },
    { icon: EventBusy, title: 'Conflict Detection', desc: 'Automatically spots scheduling conflicts across teams and leagues so you can plan ahead.' },
    { icon: Map, title: 'Travel & Locations', desc: 'Directions, travel time, and weather forecast for every game at a glance.' },
    { icon: EmojiEvents, title: 'Smart Analytics', desc: 'League analytics, power rankings, and playoff qualification — calculated automatically.' },
  ];

  const benefits = [
    { text: 'Centralized view of all players\u2019 schedules in one place' },
    { text: 'Always in sync with league and tournament changes' },
    { text: 'Spots scheduling conflicts when your players\u2019 games overlap' },
  ];

  return (
    <Box sx={{ bgcolor: 'white', fontFamily: FONT }}>
      {/* ─── Hero ───────────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 10, md: 14 }, position: 'relative', overflow: 'hidden' }}>
        {/* Subtle gradient mesh bg */}
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 0,
          background: `
            radial-gradient(ellipse 80% 60% at 20% 20%, rgba(16,185,129,0.08) 0%, transparent 60%),
            radial-gradient(ellipse 60% 50% at 80% 60%, rgba(59,130,246,0.06) 0%, transparent 60%),
            linear-gradient(to bottom, #fafbfc 0%, white 100%)
          `,
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 6, md: 8 }, alignItems: 'center' }}>
            {/* Left */}
            <Box sx={{ animation: 'nl-fade-up 0.8s ease both' }}>
              <Typography
                variant="h1"
                sx={{
                  fontFamily: FONT, fontWeight: 800,
                  fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.2rem', lg: '3.6rem' },
                  lineHeight: 1.08, letterSpacing: '-0.03em',
                  color: '#0f172a', mb: 3,
                }}
              >
                Bring all your{' '}
                <Box component="span" sx={{ color: ACCENT }}>sports</Box>{' '}
                together
              </Typography>

              <Typography sx={{
                fontFamily: FONT, fontSize: { xs: '1.05rem', md: '1.2rem' },
                color: 'text.secondary', lineHeight: 1.6, mb: 5, maxWidth: 480,
              }}>
                Youth sports schedules, teams, and updates for your entire family in one place.
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 5 }}>
                <Button
                  size="large"
                  variant="contained"
                  endIcon={signupsEnabled ? <ArrowForwardIcon /> : undefined}
                  sx={{
                    ...pill, bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK },
                    boxShadow: `0 4px 14px ${ACCENT}40`, py: 1.5, px: 4, fontSize: '1rem',
                  }}
                  onClick={handleGetStarted}
                  disabled={!signupsEnabled && !waitlistEnabled}
                >
                  {signupsEnabled ? 'Get started free' : waitlistEnabled ? 'Join the waitlist' : 'Coming soon'}
                </Button>
                <Button
                  size="large"
                  variant="outlined"
                  startIcon={<PlayArrowIcon />}
                  sx={{
                    ...pill, borderColor: 'rgba(0,0,0,0.15)', color: 'text.primary',
                    '&:hover': { borderColor: 'rgba(0,0,0,0.3)', bgcolor: 'rgba(0,0,0,0.02)' },
                    py: 1.5, px: 4, fontSize: '1rem',
                  }}
                  onClick={() => setVideoDialogOpen(true)}
                >
                  Watch demo
                </Button>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {['Setup in minutes', 'Works on any device', 'Always up to date'].map((t) => (
                  <Box key={t} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Check sx={{ fontSize: 18, color: ACCENT }} />
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.875rem', color: 'text.secondary' }}>{t}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Right — product screenshot */}
            <Box sx={{ animation: 'nl-fade-up 0.8s 0.15s ease both', opacity: 0 }}>
              <Box sx={{
                aspectRatio: '4/3', borderRadius: '12px', overflow: 'hidden',
                boxShadow: '0 24px 48px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06)',
                border: '1px solid rgba(0,0,0,0.06)',
                position: 'relative',
              }}>
                <ImageCarousel />
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ─── Supported leagues marquee ─────────────────────────── */}
      {LEAGUE_LOGOS.length > 0 && (
        <RevealBox>
          <Box sx={{ py: { xs: 4, md: 5 }, bgcolor: 'white', borderTop: '1px solid rgba(0,0,0,0.04)', borderBottom: '1px solid rgba(0,0,0,0.04)', overflow: 'hidden' }}>
            <Typography sx={{
              fontFamily: FONT, fontSize: '0.8rem', fontWeight: 600,
              textTransform: 'uppercase', letterSpacing: '0.08em',
              color: 'text.disabled', textAlign: 'center', mb: 3,
            }}>
              Connects with leagues like
            </Typography>
            <Box sx={{ position: 'relative', '&::before, &::after': { content: '""', position: 'absolute', top: 0, bottom: 0, width: 80, zIndex: 1 }, '&::before': { left: 0, background: 'linear-gradient(to right, white, transparent)' }, '&::after': { right: 0, background: 'linear-gradient(to left, white, transparent)' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 6, md: 8 }, animation: 'nl-marquee 40s linear infinite', width: 'max-content', px: { xs: 3, md: 4 } }}>
                {[...LEAGUE_LOGOS, ...LEAGUE_LOGOS].map(({ src, kind, alt }, i) => {
                  const size = LOGO_SIZE[kind];
                  return (
                    <Box
                      key={i}
                      component="img"
                      src={src}
                      alt={alt}
                      loading="lazy"
                      sx={{
                        flex: '0 0 auto',
                        height: size.h,
                        width: 'auto',
                        ...(size.maxW != null && { maxWidth: size.maxW }),
                        objectFit: 'contain',
                        filter: 'grayscale(100%)',
                        opacity: 0.5,
                        transition: 'all 0.3s',
                        '&:hover': { filter: 'grayscale(0%)', opacity: 1 },
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          </Box>
        </RevealBox>
      )}

      {/* ─── Features — bento-ish grid ──────────────────────────── */}
      <Box sx={{ py: { xs: 10, md: 14 }, bgcolor: SURFACE }}>
        <Container maxWidth="lg">
          <RevealBox>
            <Box sx={{ textAlign: 'center', maxWidth: 560, mx: 'auto', mb: { xs: 6, md: 8 } }}>
              <Typography sx={{
                fontFamily: FONT, fontWeight: 800, letterSpacing: '-0.02em',
                fontSize: { xs: '1.8rem', md: '2.4rem' }, color: '#0f172a', mb: 2,
              }}>
                Everything you need for&nbsp;game&nbsp;day
              </Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: '1.1rem', color: 'text.secondary', lineHeight: 1.6 }}>
                Schedules, updates, and analysis for games and tournaments.
              </Typography>
            </Box>
          </RevealBox>

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr 1fr' },
            gap: 3,
          }}>
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <RevealBox key={f.title} delay={i * 0.1}>
                  <Box sx={{
                    bgcolor: 'white', borderRadius: '12px', p: 4,
                    border: '1px solid rgba(0,0,0,0.06)',
                    height: '100%',
                    transition: 'box-shadow 0.25s ease, transform 0.25s ease',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                      transform: 'translateY(-2px)',
                    },
                  }}>
                    <Box sx={{
                      width: 44, height: 44, borderRadius: '10px',
                      bgcolor: `${ACCENT}14`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      mb: 2.5,
                    }}>
                      <Icon sx={{ fontSize: 22, color: ACCENT }} />
                    </Box>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '1.05rem', mb: 1, color: '#0f172a' }}>
                      {f.title}
                    </Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.95rem', color: '#475569', lineHeight: 1.6 }}>
                      {f.desc}
                    </Typography>
                  </Box>
                </RevealBox>
              );
            })}
          </Box>
        </Container>
      </Box>

      {/* ─── Benefits ───────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 10, md: 14 } }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 6, md: 10 }, alignItems: 'center' }}>
            <RevealBox>
              <Typography sx={{
                fontFamily: FONT, fontWeight: 800, letterSpacing: '-0.02em',
                fontSize: { xs: '1.8rem', md: '2.4rem' }, color: '#0f172a', mb: 2,
              }}>
                No more schedule chaos
              </Typography>
              <Typography sx={{
                fontFamily: FONT, fontSize: '1.05rem', color: 'text.secondary', lineHeight: 1.6, mb: 5,
              }}>
                We built Player Central because having multiple players in multiple leagues should be easy.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {benefits.map((b, i) => (
                  <RevealBox key={i} delay={i * 0.1}>
                    <Box sx={{
                      display: 'flex', gap: 2, alignItems: 'flex-start',
                      bgcolor: SURFACE, borderRadius: '10px', p: 2.5,
                      border: '1px solid rgba(0,0,0,0.04)',
                    }}>
                      <Box sx={{
                        width: 28, height: 28, borderRadius: 99, flexShrink: 0,
                        bgcolor: `${ACCENT}18`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        mt: 0.25,
                      }}>
                        <Check sx={{ fontSize: 16, color: ACCENT }} />
                      </Box>
                      <Typography sx={{ fontFamily: FONT, fontSize: '0.95rem', color: '#1e293b', lineHeight: 1.5 }}>
                        {b.text}
                      </Typography>
                    </Box>
                  </RevealBox>
                ))}
              </Box>
            </RevealBox>

            {/* Product screenshot instead of stock photo */}
            <RevealBox delay={0.15}>
              <Box sx={{
                aspectRatio: '4/3', borderRadius: '12px', overflow: 'hidden',
                boxShadow: '0 24px 48px rgba(0,0,0,0.1)',
                border: '1px solid rgba(0,0,0,0.06)',
              }}>
                <img
                  src="/screenshots/schedules.png"
                  alt="Schedule calendar view"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            </RevealBox>
          </Box>
        </Container>
      </Box>

      {/* ─── CTA — minimal dark ─────────────────────────────────── */}
      <Box sx={{ py: { xs: 10, md: 14 }, bgcolor: '#0f172a' }}>
        <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
          <RevealBox>
            <Typography sx={{
              fontFamily: FONT, fontWeight: 800, letterSpacing: '-0.02em',
              fontSize: { xs: '1.8rem', md: '2.4rem' }, color: 'white', mb: 2,
            }}>
              Ready to actually enjoy the season?
            </Typography>
            <Typography sx={{
              fontFamily: FONT, fontSize: '1.1rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, mb: 5,
            }}>
              Get started in minutes. No credit card required.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, justifyContent: 'center', mb: 5 }}>
              <Button
                size="large"
                variant="contained"
                endIcon={signupsEnabled ? <ArrowForwardIcon /> : undefined}
                sx={{
                  ...pill, bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK },
                  boxShadow: `0 4px 14px ${ACCENT}40`, py: 1.5, px: 4, fontSize: '1rem',
                  '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.5)' },
                }}
                onClick={handleGetStarted}
                disabled={!signupsEnabled && !waitlistEnabled}
              >
                {signupsEnabled ? 'Get started free' : waitlistEnabled ? 'Join the waitlist' : 'Coming soon'}
              </Button>
              {canInstall && (
                <Button
                  size="large"
                  variant="outlined"
                  startIcon={<GetApp />}
                  sx={{
                    ...pill, borderColor: 'rgba(255,255,255,0.2)', color: 'white',
                    '&:hover': { borderColor: 'rgba(255,255,255,0.4)', bgcolor: 'rgba(255,255,255,0.05)' },
                    py: 1.5, px: 4, fontSize: '1rem',
                  }}
                  onClick={install}
                >
                  Install app
                </Button>
              )}
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 4 }}>
              {[
                { icon: <Schedule sx={{ fontSize: 18 }} />, text: '5 min setup' },
                { icon: <Security sx={{ fontSize: 18 }} />, text: 'Private by default' },
                { icon: <Bolt sx={{ fontSize: 18 }} />, text: 'Fast & accurate' },
              ].map((item) => (
                <Box key={item.text} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ color: 'rgba(255,255,255,0.6)' }}>{item.icon}</Box>
                  <Typography sx={{ fontFamily: FONT, fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)' }}>
                    {item.text}
                  </Typography>
                </Box>
              ))}
            </Box>
          </RevealBox>
        </Container>
      </Box>

      <Footer />

      {/* ─── Video dialog ───────────────────────────────────────── */}
      <Dialog
        open={videoDialogOpen}
        onClose={() => setVideoDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'black', overflow: 'visible',
            '& .MuiDialogContent-root': { p: 0 },
            '&:hover .nl-video-close': { opacity: 1 },
          },
        }}
      >
        <DialogContent sx={{ position: 'relative' }}>
          <IconButton
            className="nl-video-close"
            onClick={() => setVideoDialogOpen(false)}
            sx={{
              position: 'absolute', top: 8, right: 8, zIndex: 1,
              bgcolor: 'rgba(0,0,0,0.6)', color: 'white',
              opacity: 0, transition: 'opacity 0.25s ease',
              backdropFilter: 'blur(4px)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
            }}
          >
            <CloseIcon />
          </IconButton>
          <video width="100%" height="auto" controls autoPlay style={{ display: 'block' }}>
            <source src="/screenshots/player_central_demo.webm" type="video/webm" />
          </video>
        </DialogContent>
      </Dialog>

      {/* ─── Waitlist dialog ────────────────────────────────────── */}
      <Dialog open={interestDialogOpen} onClose={handleCloseInterestDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontFamily: FONT }}>
            <HowToRegIcon /> Join the Waitlist
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontFamily: FONT }}>
            Enter your email to join the waitlist and be notified when we're ready for you.
          </Typography>
          {interestError && <Alert severity="error" sx={{ mb: 2 }}>{interestError}</Alert>}
          {interestSuccess ? (
            <Alert severity="success" sx={{ mb: 2 }}>{interestSuccess}</Alert>
          ) : (
            <TextField
              autoFocus fullWidth label="Email Address" type="email"
              value={interestEmail}
              onChange={(e) => setInterestEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !interestLoading) handleRegisterInterest(); }}
              disabled={interestLoading}
              placeholder="you@example.com"
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseInterestDialog} startIcon={interestSuccess ? <CloseIcon /> : <CancelIcon />}>
            {interestSuccess ? 'Close' : 'Cancel'}
          </Button>
          {!interestSuccess && (
            <Button
              onClick={handleRegisterInterest}
              variant="contained"
              disabled={interestLoading || !interestEmail}
              startIcon={interestLoading ? undefined : <HowToRegIcon />}
              sx={{ ...pill, bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK }, boxShadow: 'none' }}
            >
              {interestLoading ? <CircularProgress size={20} /> : 'Join the Waitlist'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LandingPage;
